import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../../firebase/config';

const COLLECTION_NAME = 'professional_development';

const MANAGER_ROLE_MATCHERS = [
  'departmental head',
  'deputy principal',
  'principal',
  'administrator',
  'school manager',
  'manager'
];

const normalize = (value) => String(value || '').trim().toLowerCase();

const slugify = (value) => normalize(value).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const createId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const getRolesFromProfile = (userProfile = {}) => {
  const roles = Array.isArray(userProfile.roles) ? userProfile.roles : [];
  if (roles.length > 0) return roles;
  if (userProfile.role) return [userProfile.role];
  return [];
};

export const inferProfessionalAudience = (userProfile = {}) => {
  const roles = getRolesFromProfile(userProfile).map(normalize);
  const isManager = roles.some((role) => MANAGER_ROLE_MATCHERS.some((matcher) => role.includes(matcher)));
  return isManager ? 'school_manager' : 'teacher';
};

export const getAudienceLabel = (userProfile = {}) =>
  inferProfessionalAudience(userProfile) === 'school_manager' ? 'School manager' : 'Teacher';

const buildLearningCatalog = (audience) => {
  if (audience === 'school_manager') {
    return [
      {
        id: 'instructional-leadership',
        title: 'Instructional Leadership for School Improvement',
        description: 'Lead teaching quality reviews, coaching cycles, and evidence-driven improvement planning.',
        category: 'Leadership & Strategy',
        level: 'Intermediate',
        estimatedHours: 8,
        modules: [
          { id: 'vision', title: 'Set a teaching and learning vision', duration: 35 },
          { id: 'walkthroughs', title: 'Run classroom walkthroughs', duration: 45 },
          { id: 'coaching', title: 'Coach teams with evidence', duration: 40 },
          { id: 'monitoring', title: 'Track improvement milestones', duration: 30 }
        ]
      },
      {
        id: 'staff-development-systems',
        title: 'Staff Development Systems',
        description: 'Build sustainable professional development systems for departments, grade teams, and new staff.',
        category: 'People Development',
        level: 'Advanced',
        estimatedHours: 6,
        modules: [
          { id: 'needs-analysis', title: 'Diagnose staff development needs', duration: 30 },
          { id: 'programmes', title: 'Design termly PD programmes', duration: 35 },
          { id: 'mentoring', title: 'Structure mentoring and induction', duration: 30 },
          { id: 'evaluation', title: 'Evaluate PD impact', duration: 25 }
        ]
      },
      {
        id: 'change-management',
        title: 'Leading Change in Schools',
        description: 'Manage curriculum change, accountability, and stakeholder communication without losing staff trust.',
        category: 'Change Management',
        level: 'Intermediate',
        estimatedHours: 5,
        modules: [
          { id: 'stakeholders', title: 'Map stakeholders and risks', duration: 20 },
          { id: 'communication', title: 'Communicate change clearly', duration: 25 },
          { id: 'resistance', title: 'Respond to resistance productively', duration: 30 },
          { id: 'follow-through', title: 'Sustain implementation routines', duration: 20 }
        ]
      }
    ];
  }

  return [
    {
      id: 'classroom-practice',
      title: 'High-Impact Classroom Practice',
      description: 'Strengthen lesson delivery, learner engagement, and routine reflective practice.',
      category: 'Teaching Practice',
      level: 'Beginner',
      estimatedHours: 6,
      modules: [
        { id: 'lesson-intent', title: 'Write clear learning intentions', duration: 20 },
        { id: 'checks', title: 'Use checks for understanding', duration: 30 },
        { id: 'participation', title: 'Increase learner participation', duration: 25 },
        { id: 'reflection', title: 'Reflect on lesson evidence', duration: 20 }
      ]
    },
    {
      id: 'assessment-feedback',
      title: 'Assessment and Feedback Strategies',
      description: 'Use assessment data and actionable feedback to improve learner progress.',
      category: 'Assessment',
      level: 'Intermediate',
      estimatedHours: 5,
      modules: [
        { id: 'task-design', title: 'Design useful assessment tasks', duration: 25 },
        { id: 'rubrics', title: 'Use rubrics and criteria well', duration: 30 },
        { id: 'feedback', title: 'Deliver feedback that moves learning', duration: 30 },
        { id: 'reteach', title: 'Reteach from assessment evidence', duration: 20 }
      ]
    },
    {
      id: 'inclusive-teaching',
      title: 'Inclusive Teaching and Support',
      description: 'Plan for diverse learner needs, differentiation, and classroom belonging.',
      category: 'Inclusion',
      level: 'Intermediate',
      estimatedHours: 7,
      modules: [
        { id: 'barriers', title: 'Spot common learning barriers', duration: 30 },
        { id: 'differentiation', title: 'Differentiate tasks and support', duration: 35 },
        { id: 'family', title: 'Work with caregivers and support teams', duration: 25 },
        { id: 'belonging', title: 'Build an inclusive classroom culture', duration: 30 }
      ]
    }
  ];
};

const buildRecommendedGoals = (audience) => {
  if (audience === 'school_manager') {
    return [
      'Strengthen instructional leadership routines',
      'Build a termly staff development programme',
      'Use appraisal evidence to target coaching support'
    ];
  }

  return [
    'Improve learner engagement in daily lessons',
    'Use assessment evidence to adapt teaching',
    'Document professional growth and classroom impact'
  ];
};

const buildPriorityAreas = (audience) => {
  if (audience === 'school_manager') {
    return ['Instructional leadership', 'Teacher coaching', 'School improvement planning', 'Staff wellbeing'];
  }

  return ['Lesson design', 'Assessment feedback', 'Inclusive practice', 'Subject mastery'];
};

const buildDefaultRecord = (userId, userProfile = {}) => {
  const audience = inferProfessionalAudience(userProfile);

  return {
    userId,
    audience,
    priorityAreas: buildPriorityAreas(audience),
    recommendedGoals: buildRecommendedGoals(audience),
    goals: [],
    activities: [],
    appraisals: {
      self: [],
      manager: []
    },
    learningCatalog: buildLearningCatalog(audience),
    enrolledPaths: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
};

const getRecordRef = (userId) => doc(db, COLLECTION_NAME, userId);

const ensureRecord = async (userId, userProfile = {}) => {
  const recordRef = getRecordRef(userId);
  const snapshot = await getDoc(recordRef);

  if (snapshot.exists()) {
    const data = snapshot.data();
    if (data.audience && Array.isArray(data.learningCatalog) && Array.isArray(data.recommendedGoals)) {
      return { ref: recordRef, data };
    }

    const merged = {
      ...buildDefaultRecord(userId, userProfile),
      ...data,
      updatedAt: serverTimestamp()
    };

    await setDoc(recordRef, merged, { merge: true });
    const refreshed = await getDoc(recordRef);
    return { ref: recordRef, data: refreshed.data() };
  }

  const defaultRecord = buildDefaultRecord(userId, userProfile);
  await setDoc(recordRef, defaultRecord, { merge: true });
  const created = await getDoc(recordRef);
  return { ref: recordRef, data: created.data() };
};

const saveRecord = async (userId, updates) => {
  const recordRef = getRecordRef(userId);
  await setDoc(recordRef, { ...updates, updatedAt: serverTimestamp() }, { merge: true });
  const snapshot = await getDoc(recordRef);
  return snapshot.data();
};

export const getProfessionalDevelopmentRecord = async (userId, userProfile = {}) => {
  if (!userId) return null;
  const { data } = await ensureRecord(userId, userProfile);
  return data;
};

export const saveDevelopmentGoal = async (userId, goalData, userProfile = {}) => {
  const { data } = await ensureRecord(userId, userProfile);
  const newGoal = {
    id: createId('goal'),
    title: goalData.title?.trim() || '',
    description: goalData.description?.trim() || '',
    priority: goalData.priority || 'Medium',
    targetDate: goalData.targetDate || '',
    progress: Number(goalData.progress || 0),
    successMeasure: goalData.successMeasure?.trim() || '',
    audience: data.audience,
    createdAt: new Date().toISOString()
  };

  return saveRecord(userId, {
    goals: [...(data.goals || []), newGoal]
  });
};

export const updateDevelopmentGoalProgress = async (userId, goalId, progress, userProfile = {}) => {
  const { data } = await ensureRecord(userId, userProfile);
  const goals = (data.goals || []).map((goal) => (
    goal.id === goalId
      ? {
          ...goal,
          progress: Math.max(0, Math.min(100, progress)),
          completedAt: progress >= 100 ? new Date().toISOString() : null
        }
      : goal
  ));

  return saveRecord(userId, { goals });
};

export const saveDevelopmentActivity = async (userId, activityData, userProfile = {}) => {
  const { data } = await ensureRecord(userId, userProfile);
  const newActivity = {
    id: createId('activity'),
    title: activityData.title?.trim() || '',
    type: activityData.type || 'Workshop',
    duration: Number(activityData.duration || 0),
    date: activityData.date || '',
    reflection: activityData.reflection?.trim() || '',
    completed: Boolean(activityData.completed),
    createdAt: new Date().toISOString()
  };

  return saveRecord(userId, {
    activities: [newActivity, ...(data.activities || [])]
  });
};

export const saveSelfAppraisal = async (userId, appraisalData, userProfile = {}) => {
  const { data } = await ensureRecord(userId, userProfile);
  const categories = appraisalData.categories || {};
  const ratings = Object.values(categories).map((value) => Number(value || 0));
  const average = ratings.length > 0
    ? Number((ratings.reduce((sum, value) => sum + value, 0) / ratings.length).toFixed(1))
    : 0;

  const newAppraisal = {
    id: createId('self'),
    period: appraisalData.period || '',
    overallRating: average,
    categories,
    reflection: appraisalData.reflection?.trim() || '',
    createdAt: new Date().toISOString()
  };

  return saveRecord(userId, {
    appraisals: {
      ...(data.appraisals || {}),
      self: [newAppraisal, ...((data.appraisals?.self) || [])],
      manager: data.appraisals?.manager || []
    }
  });
};

export const enrolLearningPath = async (userId, pathId, userProfile = {}) => {
  const { data } = await ensureRecord(userId, userProfile);
  const existing = (data.enrolledPaths || []).find((path) => path.sourcePathId === pathId);
  if (existing) return data;

  const sourcePath = (data.learningCatalog || []).find((path) => path.id === pathId);
  if (!sourcePath) {
    throw new Error('Learning pathway not found');
  }

  const newEnrollment = {
    id: createId(`path_${slugify(pathId) || 'path'}`),
    sourcePathId: sourcePath.id,
    title: sourcePath.title,
    category: sourcePath.category,
    startedAt: new Date().toISOString().slice(0, 10),
    completedModules: 0,
    totalModules: sourcePath.modules.length,
    progress: 0,
    certificateEarned: false,
    modules: sourcePath.modules.map((module) => ({
      ...module,
      completed: false,
      completedAt: null
    }))
  };

  return saveRecord(userId, {
    enrolledPaths: [newEnrollment, ...(data.enrolledPaths || [])]
  });
};

export const toggleLearningModuleCompletion = async (userId, enrollmentId, moduleId, userProfile = {}) => {
  const { data } = await ensureRecord(userId, userProfile);
  const enrolledPaths = (data.enrolledPaths || []).map((path) => {
    if (path.id !== enrollmentId) return path;

    const modules = (path.modules || []).map((module) => (
      module.id === moduleId
        ? {
            ...module,
            completed: !module.completed,
            completedAt: !module.completed ? new Date().toISOString() : null
          }
        : module
    ));

    const completedModules = modules.filter((module) => module.completed).length;
    const progress = modules.length > 0 ? Math.round((completedModules / modules.length) * 100) : 0;

    return {
      ...path,
      modules,
      completedModules,
      progress,
      certificateEarned: progress === 100
    };
  });

  return saveRecord(userId, { enrolledPaths });
};
